import { Message } from '@ai-sdk/react';
import React from 'react';
import DefaultWelcomeScreen from './welcome-screens/DefaultWelcomeScreen';
import WelcomeAccountOverview from './welcome-screens/WelcomeAccountOverview';
import { WelcomeAccountOverviewSkeleton } from './welcome-screens/WelcomeAccountOverviewSkeleton';

type AccountOverviewComponent = {
  type: 'account-overview';
  data: any;
  isLoading?: boolean;
};

type CustomComponent = {
  type: 'component';
  component: React.ReactNode;
};

type WelcomeComponentConfig =
  | React.ReactNode
  | AccountOverviewComponent
  | CustomComponent;

interface WelcomeWrapperProps {
  messages: Message[];
  isWidgetChat?: boolean;
  evmAddress?: string;
  append?: (message: Message) => void;
  options?: {
    customComponents?: {
      welcomeMessageComponent?: WelcomeComponentConfig;
    };
    route?: string;
  };
}

const renderTypedComponent = (
  component: AccountOverviewComponent | CustomComponent,
  evmAddress?: string,
  append?: (message: Message) => void,
): React.ReactNode => {
  if (component.type === 'account-overview' && evmAddress && append) {
    if (component.isLoading || !component.data) {
      return <WelcomeAccountOverviewSkeleton />;
    }

    return (
      <WelcomeAccountOverview
        evmAddress={evmAddress}
        data={component.data}
        append={append}
      />
    );
  }

  if (component.type === 'component' && 'component' in component) {
    return component.component;
  }

  return null;
};

export const WelcomeWrapper: React.FC<WelcomeWrapperProps> = ({
  messages,
  isWidgetChat,
  evmAddress,
  append,
  options,
}) => {
  if (isWidgetChat || !messages || messages.length > 0) {
    return null;
  }

  const welcomeComponent = options?.customComponents?.welcomeMessageComponent;
  const route = options?.route;

  if (!welcomeComponent) {
    // Show skeleton only for core-agent route when wallet is connected
    if (route === '/core-agent' && evmAddress && append) {
      return <WelcomeAccountOverviewSkeleton />;
    }
    return <DefaultWelcomeScreen />;
  }

  if (
    typeof welcomeComponent === 'object' &&
    welcomeComponent !== null &&
    'type' in welcomeComponent
  ) {
    return renderTypedComponent(
      welcomeComponent as AccountOverviewComponent | CustomComponent,
      evmAddress,
      append,
    );
  }

  return welcomeComponent as React.ReactNode;
};
